import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";

export const CONVERSATIONS_POLL_MS = 15000;
export const MESSAGES_POLL_MS = 5000;
const PAGE_SIZE = 50;

const deliveryStatusSchema = z.enum(["sent", "delivered", "read", "failed"]).nullable();

export const whatsappMessageSchema = z.object({
  id: z.string(),
  wa_message_id: z.string(),
  direction: z.enum(["in", "out"]),
  type: z.string(),
  body: z.string(),
  status: deliveryStatusSchema,
  error: z.string().nullable(),
  at: z.string(),
  status_at: z.string().nullable(),
  sent_by: z.string().nullable(),
});
export type WhatsAppMessage = z.infer<typeof whatsappMessageSchema>;

const contactSchema = z.object({
  wa_id: z.string(),
  name: z.string().nullable(),
  unread_count: z.number().int().min(0),
  window_open: z.boolean(),
  window_expires_at: z.string().nullable(),
  opted_out: z.boolean(),
  lead_id: z.string().nullable(),
});
export type WhatsAppContact = z.infer<typeof contactSchema>;

const conversationSchema = contactSchema.extend({
  last_message: z
    .object({ body: z.string(), direction: z.enum(["in", "out"]), type: z.string(), status: deliveryStatusSchema, at: z.string() })
    .nullable(),
  last_message_at: z.string(),
});
export type WhatsAppConversation = z.infer<typeof conversationSchema>;

const originSchema = z.object({
  ref: z.string().nullable(),
  canal: z.string(),
  utm_campaign: z.string().nullable(),
  utm_content: z.string().nullable(),
  ean: z.string().nullable(),
  marca: z.string().nullable(),
  producto: z.string().nullable(),
  clicked_at: z.string(),
});
export type WhatsAppOrigin = z.infer<typeof originSchema>;

const conversationsSchema = z.object({ items: z.array(conversationSchema), sender_configured: z.boolean() });
const messagesSchema = z.object({
  contact: contactSchema,
  items: z.array(whatsappMessageSchema),
  has_more: z.boolean(),
  sender_configured: z.boolean(),
  origen: originSchema.optional(),
});
export type WhatsAppMessagesPage = z.infer<typeof messagesSchema>;

export const detectedOrderSchema = z.object({
  id: z.string(),
  productos: z.string().nullable(),
  importe_eur: z.number().nullable(),
  frase: z.string().nullable(),
  estado: z.enum(["detectado", "confirmado", "cancelado"]),
  detected_at: z.string(),
  decided_by: z.string().nullable(),
  decided_at: z.string().nullable(),
});
export type DetectedOrder = z.infer<typeof detectedOrderSchema>;

const ordersSchema = z.object({ items: z.array(detectedOrderSchema) });
const pendingOrdersSchema = z.object({ wa_ids: z.array(z.string()) });
const decisionSchema = z.object({ pedido: detectedOrderSchema });

const accountEventSchema = z.object({
  id: z.string(),
  event_type: z.string(),
  severity: z.enum(["info", "warning", "critical"]),
  summary: z.string(),
  occurred_at: z.string(),
});
export type WhatsAppAccountEvent = z.infer<typeof accountEventSchema>;
const accountEventsSchema = z.object({ items: z.array(accountEventSchema) });

const replySchema = z.object({ message: whatsappMessageSchema });
const readSchema = z.object({ unread_count: z.number(), receipt_sent: z.boolean() });

const conversationPath = (waId: string) => `/whatsapp/conversations/${encodeURIComponent(waId)}`;

export function useWhatsAppConversations(businessId: string, search: string) {
  return useQuery({
    queryKey: ["whatsapp-conversations", businessId, search],
    enabled: Boolean(businessId),
    queryFn: () => apiClient.get("/whatsapp/conversations", conversationsSchema, { business_id: businessId, q: search || undefined }),
    refetchInterval: CONVERSATIONS_POLL_MS,
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/** Última página de la conversación abierta, refrescada cada 5 s. */
export function useWhatsAppMessages(businessId: string, waId: string | null) {
  return useQuery({
    queryKey: ["whatsapp-messages", businessId, waId],
    enabled: Boolean(businessId && waId),
    queryFn: () => fetchWhatsAppMessages(businessId, waId ?? "", null),
    refetchInterval: MESSAGES_POLL_MS,
    retry: false,
  });
}

export function fetchWhatsAppMessages(businessId: string, waId: string, before: string | null) {
  return apiClient.get(`${conversationPath(waId)}/messages`, messagesSchema, {
    business_id: businessId,
    before: before ?? undefined,
    limit: PAGE_SIZE,
  });
}

export function useSendWhatsAppReply(businessId: string, waId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => apiClient.post(`${conversationPath(waId ?? "")}/messages`, replySchema, { text }, { business_id: businessId }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["whatsapp-messages", businessId, waId] });
      void client.invalidateQueries({ queryKey: ["whatsapp-conversations", businessId] });
    },
  });
}

export function useMarkWhatsAppRead(businessId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (waId: string) => apiClient.post(`${conversationPath(waId)}/read`, readSchema, undefined, { business_id: businessId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["whatsapp-conversations", businessId] });
    },
  });
}

export function useWhatsAppAccountEvents(businessId: string) {
  return useQuery({
    queryKey: ["whatsapp-account-events", businessId],
    enabled: Boolean(businessId),
    queryFn: () => apiClient.get("/whatsapp/account-events", accountEventsSchema, { business_id: businessId, limit: 20 }),
    refetchInterval: 60000,
    retry: false,
  });
}

/** Pedidos detectados de la conversación abierta: la tarjeta se refresca junto con los mensajes. */
export function useWhatsAppOrders(businessId: string, waId: string | null) {
  return useQuery({
    queryKey: ["whatsapp-orders", businessId, waId],
    enabled: Boolean(businessId && waId),
    queryFn: () =>
      apiClient.get(`${conversationPath(waId ?? "")}/orders`, ordersSchema, { business_id: businessId }),
    refetchInterval: MESSAGES_POLL_MS,
    retry: false,
  });
}

/** Contactos con un pedido detectado sin decidir: la marca «pedido» de la lista de chats. */
export function useWhatsAppPendingOrders(businessId: string) {
  return useQuery({
    queryKey: ["whatsapp-orders-pending", businessId],
    enabled: Boolean(businessId),
    queryFn: () => apiClient.get("/whatsapp/orders/pending", pendingOrdersSchema, { business_id: businessId }),
    refetchInterval: CONVERSATIONS_POLL_MS,
    retry: false,
  });
}

export function useDecideWhatsAppOrder(businessId: string, waId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, decision }: { orderId: string; decision: "confirmado" | "cancelado" }) =>
      apiClient.post(`/whatsapp/orders/${encodeURIComponent(orderId)}/decision`, decisionSchema, { decision }, { business_id: businessId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["whatsapp-orders", businessId, waId] });
      void client.invalidateQueries({ queryKey: ["whatsapp-orders-pending", businessId] });
    },
  });
}
